import React from "react";
import { Link } from "react-router-dom";
import { Scale, BookOpen } from "lucide-react";

interface EntityLinkedTextProps {
  text: string;
  className?: string;
}

// Regex to capture:
// 1. Explicit tags: [rule:<uuid>] or [knowledge:<uuid>]
// 2. Prosaic references: Under rule [<uuid>] or rule [<uuid>] or knowledge [<uuid>]
// 3. Standalone bracketed UUIDs: [<uuid>]
const ENTITY_REGEX =
  /(?:\[(?:rule|knowledge):([0-9a-fA-F-]{36})\])|(?:\b(?:rule|directive):?\s*\[?([0-9a-fA-F-]{36})\]?)|(?:\b(?:knowledge|policy):?\s*\[?([0-9a-fA-F-]{36})\]?)|(?:\[([0-9a-fA-F-]{36})\])/gi;

export const EntityLinkedText: React.FC<EntityLinkedTextProps> = ({
  text,
  className = "",
}) => {
  if (!text) return null;

  const elements: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset regex index
  ENTITY_REGEX.lastIndex = 0;

  while ((match = ENTITY_REGEX.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = ENTITY_REGEX.lastIndex;

    // Push preceding text
    if (matchStart > lastIndex) {
      elements.push(text.substring(lastIndex, matchStart));
    }

    const fullMatch = match[0];
    const explicitRuleOrKnowledgeUuid = match[1];
    const ruleUuid = match[2];
    const knowledgeUuid = match[3];
    const bareUuid = match[4];

    let entityType: "rule" | "knowledge" = "rule";
    let uuid = "";

    if (explicitRuleOrKnowledgeUuid) {
      uuid = explicitRuleOrKnowledgeUuid;
      entityType = fullMatch.toLowerCase().startsWith("[knowledge:") ? "knowledge" : "rule";
    } else if (ruleUuid) {
      uuid = ruleUuid;
      entityType = "rule";
    } else if (knowledgeUuid) {
      uuid = knowledgeUuid;
      entityType = "knowledge";
    } else if (bareUuid) {
      uuid = bareUuid;
      // Default bare bracketed UUIDs in evaluator reasoning to rule unless preceded by knowledge keyword
      const prefix = text.substring(Math.max(0, matchStart - 15), matchStart).toLowerCase();
      if (prefix.includes("knowledge") || prefix.includes("item")) {
        entityType = "knowledge";
      } else {
        entityType = "rule";
      }
    }

    const targetUrl = entityType === "rule" ? `/rules/${uuid}` : `/knowledge/${uuid}`;
    const Icon = entityType === "rule" ? Scale : BookOpen;
    const label = `${entityType === "rule" ? "Rule" : "Knowledge"} [${uuid.slice(0, 8)}...]`;

    elements.push(
      <Link
        key={`${matchStart}-${uuid}`}
        to={targetUrl}
        title={`View ${entityType === "rule" ? "Rule" : "Knowledge Item"} (${uuid})`}
        className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 font-mono font-medium hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className="w-3 h-3 shrink-0" />
        <span>{label}</span>
      </Link>
    );

    lastIndex = matchEnd;
  }

  // Push remaining text
  if (lastIndex < text.length) {
    elements.push(text.substring(lastIndex));
  }

  return <span className={className}>{elements}</span>;
};
